import './styles.scss'

import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Form, Grid, Input, Row } from 'antd'
import clsx from 'clsx'

import FormatPrice from '@/components/FormatPrice'
import { useAppSelector } from '@/store/hook'
import { selectCartData } from '@/store/selectors/cartSelector'
import { selectHideVoucher } from '@/store/selectors/configSelector'
import usePermission from '@/hooks/usePermission'
import { useCheckoutContext } from '@/contexts/checkout/useCheckoutContext'
import { MODULE_CART_CHECKOUT_RIGHTS } from '@/libs/constants/modulerights'
import { checkObjectIsEmpty } from '@/libs/utils/common'

import CostSummary from '../CostSummary'

function LowerSection({ form }) {
  const { t } = useTranslation()

  const { md } = Grid.useBreakpoint()

  const {
    currencyUnit,
    cartTotal,
    voucherValue,
    checkoutSettings,
    vat,
    cartTotalVAT,
  } = useAppSelector(selectCartData)
  const hideVoucher = useAppSelector(selectHideVoucher)

  const shippingNotes = checkoutSettings?.shippingNotes
  const { isShippingNotesMandatory } = useCheckoutContext()
  const { hasPermission: hasViewIndividualPricePermission } = usePermission(
    MODULE_CART_CHECKOUT_RIGHTS.VIEW_INDIVIDUAL_PRICE,
  )

  const renderShippingNotes = useCallback(() => {
    return (
      shippingNotes && (
        <Col
          lg={12}
          md={12}
          sm={24}
          xl={12}
          xs={24}
          className={clsx('order-overview__lower-section__left-part', {
            'fw-container': !md,
          })}
        >
          <Form.Item
            className="custom-form-item order-overview__lower-section__left-part__input"
            label={t('checkout.step3.lowerSection.shippingNotes.title')}
            name="shippingNotesStep3"
            rules={[
              {
                message: t(
                  'checkout.step3.lowerSection.shippingNotes.isRequired',
                ),
                required: isShippingNotesMandatory,
              },
            ]}
          >
            <Input.TextArea
              allowClear
              rows={4}
              placeholder={t(
                'checkout.step3.lowerSection.shippingNotes.description',
              )}
              onChange={(event) => {
                form.setFieldsValue({
                  shippingNotes: event.target.value,
                })
              }}
            />
          </Form.Item>
        </Col>
      )
    )
  }, [md, t, form, shippingNotes, isShippingNotesMandatory])

  const costSummaryColumnAttributes = useMemo(() => {
    return checkoutSettings?.shippingNotes
      ? { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 }
      : {
        span: 24,
        md: { span: 12, push: 12 },
      }
  }, [checkoutSettings.shippingNotes])

  const renderPriceSection = useCallback(
    (title, price) => (
      <Row>
        <Col
          className="order-overview__lower-section__bottom-bar__title"
          lg={14}
          md={14}
          sm={14}
          xl={14}
          xs={14}
        >
          {title}
        </Col>
        <Col
          className="order-overview__lower-section__bottom-bar__price"
          lg={10}
          md={10}
          sm={10}
          xl={10}
          xs={10}
        >
          <FormatPrice currencyUnit={currencyUnit} price={price} />
        </Col>
      </Row>
    ),
    [currencyUnit],
  )

  const shouldRenderVoucherSavings = () => {
    const isVoucherValueEmpty = checkObjectIsEmpty(voucherValue)
    const isVoucherDiscountEqualZero = !isVoucherValueEmpty && +voucherValue?.totalDiscount === 0

    if (isVoucherValueEmpty || isVoucherDiscountEqualZero) {
      return null
    }

    return (
      <Row>
        <Col
          className="order-overview__lower-section__bottom-bar__title"
          lg={14}
          md={14}
          sm={14}
          xl={14}
          xs={14}
        >
          {/* {t('checkout.step3.upperSection.rightPart.total')} */}
          {t('voucher.fields.voucherCode')}
          <div>{voucherValue?.code}</div>
        </Col>
        <Col
          className="order-overview__lower-section__bottom-bar__price voucher-value"
          hidden={!hasViewIndividualPricePermission}
          lg={10}
          md={10}
          sm={10}
          xl={10}
          xs={10}
        >
          <FormatPrice
            currencyUnit={currencyUnit}
            price={voucherValue?.totalDiscount}
          />
        </Col>
      </Row>
    )
  }

  // const renderTotal = () => {
  //   return (
  //     <>
  //       {md && <Col xs={24} sm={24} md={12} lg={12} xl={12} />}
  //       <Col xs={24} sm={24} md={12} lg={12} xl={12}>
  //         {checkoutSettings?.includingVAT && showVATEnabled
  //           ? (
  //               <>
  //                 {renderPriceSection(
  //                   t("checkout.step3.upperSection.rightPart.total"),
  //                   cartTotal,
  //                 )}
  //                 {shouldRenderVoucherSavings()}
  //                 {renderPriceSection(t("vat"), vat)}
  //                 {renderPriceSection(t("totalWithVat"), cartTotalVAT)}
  //               </>
  //             )
  //           : (
  //               <>
  //                 {shouldRenderVoucherSavings()}
  //                 {renderPriceSection(
  //                   t("checkout.step3.upperSection.rightPart.total"),
  //                   cartTotal,
  //                 )}
  //               </>
  //             )}
  //       </Col>
  //     </>
  //   );
  // };

  return (
    <section className="order-overview__lower-section__wrapper">
      <Row>
        {renderShippingNotes()}
        <Col
          className="order-overview__lower-section__right-part"
          hidden={!hasViewIndividualPricePermission}
          {...costSummaryColumnAttributes}
        >
          <CostSummary form={form} subtitleRight={md} />
        </Col>
      </Row>
      <Row
        className="order-overview__lower-section__bottom-bar"
        hidden={!hasViewIndividualPricePermission}
      >
        {md && <Col lg={12} md={12} sm={24} xl={12} xs={24} />}
        <Col lg={12} md={12} sm={24} xl={12} xs={24}>
          {renderPriceSection(
            t('checkout.step3.upperSection.rightPart.total'),
            cartTotal,
          )}
          {!hideVoucher && shouldRenderVoucherSavings()}

          {checkoutSettings?.includingVAT && (
            <>
              {renderPriceSection(t('vat'), vat)}
              {renderPriceSection(t('totalWithVat'), cartTotalVAT)}
            </>
          )}
        </Col>
      </Row>
    </section>
  )
}

export default LowerSection
